import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { trpc, type RouterOutputs } from '@org/clients/school';
import { FeatureFlagGate } from '../../../components/feature-flag-gate';
import {
  formatPrice,
  monthsFromDays,
  posterInkForProduct,
  posterStateOf,
} from '../../../components/products/helpers';
import { Pill, Poster, PosterCta, PriceSticker } from '../../../components/products/poster';
import { BaseHero } from '../../../components/products/base-hero';
import { useAccess } from '../../../components/access/use-access';
import {
  P,
  PRODUCTS_STYLES,
  eyebrowStyle,
  h1Style,
  oswald,
  subStyle,
  useWidth,
} from '../../../components/products/theme';

export const Route = createFileRoute('/_app/products/')({
  // `paid=1`: back from the ASAAS payment page. Optional, so
  // `<Link to="/products">` needs no `search` prop.
  validateSearch: (search: Record<string, unknown>): { paid?: 1 } => ({
    ...(search.paid === 1 || search.paid === '1' ? { paid: 1 as const } : {}),
  }),
  component: GatedProductsPage,
});

function GatedProductsPage() {
  return (
    <FeatureFlagGate flag="journey-products">
      <ProductsPage />
    </FeatureFlagGate>
  );
}

// Output type straight from the router (see journey.tsx's same pattern).
type ProductItem = RouterOutputs['products']['listActive'][number];

// -- One cartaz on the wall --
function WallPoster({ product }: { product: ProductItem }) {
  const { t, i18n } = useTranslation('p2e');
  const navigate = useNavigate();
  const dateLocale = i18n.language?.startsWith('pt') ? ptBR : enUS;

  const state = posterStateOf(product);
  const level = product.journeyLevelName;
  const endsAtLabel = product.endsAt
    ? format(new Date(product.endsAt), 'PP', { locale: dateLocale })
    : null;
  const months = monthsFromDays(product.defaultDurationDays);

  // Top-left: level · window. Owned posters show the real end date.
  const window =
    state === 'owned'
      ? endsAtLabel
        ? t('products.untilDate', { date: endsAtLabel })
        : t('products.noTimeLimit')
      : state === 'expired' && endsAtLabel
        ? t('products.endedDate', { date: endsAtLabel })
        : months == null
          ? t('products.noTimeLimit')
          : t('products.months', { count: months });
  const topLeft = [level, window].filter(Boolean).join(' · ');

  const pill =
    state === 'owned' ? (
      <Pill tone="ok">{t('products.pill.owned')}</Pill>
    ) : state === 'expired' ? (
      <Pill tone="exp">{t('products.pill.expired')}</Pill>
    ) : state === 'pending' ? (
      <Pill tone="wait">{t('products.pill.pending')}</Pill>
    ) : (
      <Pill tone="new">{t('products.pill.new')}</Pill>
    );

  const stamp =
    state === 'owned'
      ? { label: t('products.stamp.owned'), tone: 'green' as const }
      : state === 'expired'
        ? { label: t('products.stamp.expired'), tone: 'grey' as const }
        : state === 'pending'
          ? { label: t('products.stamp.pending'), tone: 'gold' as const, dashed: true }
          : undefined;

  const priceLabel =
    product.priceCents === 0
      ? t('products.free')
      : formatPrice(product.priceCents, product.currency, i18n.language ?? 'pt');

  const footRight =
    state === 'new' ? (
      <PriceSticker>{priceLabel}</PriceSticker>
    ) : state === 'owned' ? (
      <PosterCta>{t('products.continue')}</PosterCta>
    ) : state === 'expired' ? (
      <PosterCta ghost>{t('products.renew')}</PosterCta>
    ) : null;

  return (
    <Poster
      ink={posterInkForProduct(product.productId)}
      coverImageUrl={product.coverImageUrl}
      headline={product.tagline || product.name}
      topLeft={topLeft}
      topRight={pill}
      footLeft={
        product.tagline
          ? `${product.name} · ${t('products.posters', { count: product.unitCount })}`
          : t('products.posters', { count: product.unitCount })
      }
      footRight={footRight}
      stamp={stamp}
      state={state}
      ariaLabel={product.name}
      onOpen={() =>
        navigate({ to: '/products/$productId', params: { productId: product.productId } })
      }
    />
  );
}

// -- Page: the wall --
function ProductsPage() {
  const { t } = useTranslation('p2e');
  const w = useWidth();
  const isMobile = w < 480;
  const navigate = useNavigate();
  const { paid } = Route.useSearch();
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.products.listActive.useQuery();
  const { data: access } = useAccess();

  // One toast per arrival, then the flag leaves the URL so a refresh is quiet.
  useEffect(() => {
    if (!paid) return;
    toast.info(t('products.checkout.processing'), { duration: 8000 });
    void utils.access.me.invalidate();
    void navigate({ to: '/products', search: {}, replace: true });
  }, [paid, navigate, t, utils]);

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: P.cream,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <style dangerouslySetInnerHTML={{ __html: PRODUCTS_STYLES }} />
        <div
          role="status"
          aria-live="polite"
          style={{
            fontFamily: oswald,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: P.warmGray,
          }}
        >
          {t('products.loading')}
        </div>
      </div>
    );
  }

  // The base access has its own hero; the wall is for the extra journeys.
  const products = (data ?? []).filter((p) => (p as { kind?: string }).kind !== 'base');
  const baseState = access?.base.state;
  const subtitle =
    access?.base.required && (baseState === 'not_started' || baseState === 'expired')
      ? t('products.base.storeSubtitle')
      : t('products.subtitle');

  return (
    <div style={{ minHeight: '100vh', background: P.cream }}>
      <style dangerouslySetInnerHTML={{ __html: PRODUCTS_STYLES }} />
      <div
        style={{
          maxWidth: 960,
          margin: '0 auto',
          padding: isMobile ? '24px 16px 48px' : '24px 16px 64px',
        }}
      >
        <div style={eyebrowStyle}>{t('products.eyebrow')}</div>
        <h1
          data-testid="products-heading"
          style={{ ...h1Style, fontSize: isMobile ? 32 : 40 }}
        >
          {t('products.title')}
        </h1>
        <p style={subStyle}>{subtitle}</p>

        <BaseHero />

        {products.length > 0 && (
          <div style={{ ...eyebrowStyle, marginTop: 6 }}>{t('products.base.extrasEyebrow')}</div>
        )}

        {products.length === 0 ? (
          <div
            style={{
              marginTop: 18,
              background: P.white,
              border: `1.5px dashed ${P.lightBorder}`,
              borderRadius: 4,
              padding: '32px 20px',
              textAlign: 'center',
              color: P.midText,
              fontSize: 15,
            }}
          >
            {t('products.empty')}
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
              gap: '22px 18px',
              // Tape strips + outlines stick out past the sheets; give them room.
              margin: '18px 0',
              padding: '10px 4px 4px',
            }}
          >
            {products.map((product) => (
              <WallPoster key={product.productId} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
